import {defineStore} from 'pinia';
import axios from 'axios';
import type {LeasingCompany, RcaVehicleForm} from "~/types/Vehicle";
import type {RcaCustomerForm} from "~/types/Customer";
import type {RcaAvailability, RcaForm, RcaInsuranceCompany} from "~/types/Rca";

export const useRcaStore = defineStore('rca', {
    state: () => ({
        availabilities: [] as RcaAvailability[],
        _fetchAvailabilitiesPromise: null as Promise<any> | null,
        leasingCompanies: [] as LeasingCompany[],
        _fetchRcaLeasingCompaniesPromise: null as Promise<any> | null,
        insuranceCompanies: [] as RcaInsuranceCompany[],
        _fetchInsuranceCompaniesPromise: null as Promise<any> | null,
        _sendOffersToUserPromise: null as Promise<any> | null,
        allInsuranceCompanies: [] as RcaInsuranceCompany[],
        _fetchAllInsuranceCompaniesPromise: null as Promise<any> | null,
        _fetchOfferPdfPromise: null as Promise<any> | null,
        _fetchPolicyPdfPromise: null as Promise<any> | null,
    }),
    actions: {
        async fetchAvailabilities() {
            if (this._fetchAvailabilitiesPromise) return this._fetchAvailabilitiesPromise;

            this._fetchAvailabilitiesPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/rca-availabilities`);
                    if (response.data.data) {
                        this.availabilities = response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchAvailabilitiesPromise = null;
                }
            })();

            return this._fetchAvailabilitiesPromise;
        },
        async getAvailabilities() {
            if (this.availabilities.length === 0) {
                await this.fetchAvailabilities();
            }
            return this.availabilities;
        },
        async fetchRcaLeasingCompanies() {
            if (this._fetchRcaLeasingCompaniesPromise) return this._fetchRcaLeasingCompaniesPromise;

            this._fetchRcaLeasingCompaniesPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/rca-leasing-companies`);
                    if (response.data.data) {
                        this.leasingCompanies = response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchRcaLeasingCompaniesPromise = null;
                }
            })();

            return this._fetchRcaLeasingCompaniesPromise;
        },
        async getRcaLeasingCompanies() {
            if (this.leasingCompanies.length === 0) {
                await this.fetchRcaLeasingCompanies();
            }
            return this.leasingCompanies;
        },
        async fetchRcaInsuranceCompanies(vehicleTypeId: number, availabilities: string) {
            if (this._fetchInsuranceCompaniesPromise) return this._fetchInsuranceCompaniesPromise;

            this._fetchInsuranceCompaniesPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/rca-insurance-companies/list?vehicle_type_id=${vehicleTypeId}&availabilities=${availabilities}`);
                    if (response.data.data) {
                        this.insuranceCompanies = response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchInsuranceCompaniesPromise = null;
                }
            })();

            return this._fetchInsuranceCompaniesPromise;
        },
        async fetchAllRcaInsuranceCompanies() {
            if (this._fetchAllInsuranceCompaniesPromise) return this._fetchAllInsuranceCompaniesPromise;

            this._fetchAllInsuranceCompaniesPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/rca-insurance-companies`);
                    if (response.data.data) {
                        this.allInsuranceCompanies = response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchAllInsuranceCompaniesPromise = null;
                }
            })();

            return this._fetchAllInsuranceCompaniesPromise;
        },
        async getAllRcaInsuranceCompanies(): Promise<RcaInsuranceCompany[]> {
            if (this.allInsuranceCompanies.length === 0) {
                await this.fetchAllRcaInsuranceCompanies();
            }
            return this.allInsuranceCompanies;
        },
        async fetchOffers(
            insuranceCompany: RcaInsuranceCompany,
            vehicleForm: RcaVehicleForm,
            customerForm: RcaCustomerForm,
            rcaForm: RcaForm,
            accessToken: string|null, token: string|undefined) {
            try {
                const config = accessToken ? {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                } : {};

                const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/v1/rca-offers`, {
                    insurance_company: insuranceCompany,
                    vehicle: vehicleForm,
                    customer: customerForm,
                    rca: rcaForm,
                    token,
                }, config);
                if (response.data.data) {
                    return response.data.data;
                }
            } catch (error) {
                return null;
            }
        },
        async sendOffersToUser(offers: { [key: string]: any },
                               insuranceCompanies: RcaInsuranceCompany[],
                               isDeducted: boolean,
                               email: string,
                               vehicleId: number,
                               accessToken: string|null,
                               token: string|undefined) {
            if (this._sendOffersToUserPromise) return this._sendOffersToUserPromise;

            this._sendOffersToUserPromise = (async () => {
                try {
                    const config = accessToken ? {
                        headers: {
                            Authorization: `Bearer ${accessToken}`
                        }
                    } : {};

                    const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/v1/rca-offers/send`, {
                        rca_offers_data: offers,
                        email,
                        is_deducted: isDeducted,
                        vehicle_id: vehicleId,
                        insurance_companies: insuranceCompanies,
                        token,
                    }, config);
                    if (response.data.data) {
                        return response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._sendOffersToUserPromise = null;
                }
            })();

            return this._sendOffersToUserPromise;
        },
        async fetchOfferPdf(offerId: number, accessToken: string|null) {
            if (this._fetchOfferPdfPromise) return this._fetchOfferPdfPromise;

            this._fetchOfferPdfPromise = (async () => {
                try {
                    const config = accessToken ? {
                        headers: {
                            Authorization: `Bearer ${accessToken}`
                        }
                    } : {};

                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/rca-offers/${offerId}/pdf`, {
                        responseType: 'blob',
                        headers: {
                            'Accept': 'application/pdf',
                            'Content-Type': 'application/pdf',
                            ...config.headers,
                        },
                    });
                    if (response.data) {
                        return response.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchOfferPdfPromise = null;
                }
            })();

            return this._fetchOfferPdfPromise;
        },
        async fetchPolicyPdf(policyId: number, accessToken: string|null) {
            if (this._fetchPolicyPdfPromise) return this._fetchPolicyPdfPromise;
            this._fetchPolicyPdfPromise = (async () => {
                try {
                    const config = accessToken ? {
                        headers: {
                            Authorization: `Bearer ${accessToken}`
                        }
                    } : {};
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/rca-policies/${policyId}/pdf`, {
                        responseType: 'blob',
                        headers: {
                            'Accept': 'application/pdf',
                            'Content-Type': 'application/pdf',
                            ...config.headers,
                        },
                    });
                    if (response.data) {
                        return response.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchPolicyPdfPromise = null;
                }
            })();
            return this._fetchPolicyPdfPromise;
        },
        async sendSmsFromPartner(accessToken: string, phoneNumber: string, token: string|undefined, partnerCode: string): Promise<boolean> {
            try {
                const config = {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                };

                const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/v1/rca-offers/send-sms-from-partner`, {
                    phone_number: phoneNumber,
                    partner_code: partnerCode,
                    token
                }, config);
                if (response.data.data) {
                    return response.data.data.is_sent as boolean;
                }
                return false;
            } catch (error) {
                return false;
            }
        },
        async sendOfferVerificationNotificationFromPartner(accessToken: string, offerId: number, token: string|undefined): Promise<boolean> {
            try {
                const config = {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                };

                const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/v1/rca-offers/send-offer-verification-notification-from-partner`, {
                    offer_id: offerId,
                    token,
                }, config);
                if (response.data.data) {
                    return response.data.data.is_sent as boolean;
                }
                return false;
            } catch (error) {
                return false;
            }
        },
        async getCodeForUuid(uuid: string): Promise<string | null> {
            try {
                const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/rca-offers/get-terms-confirm-code-for-uuid?uuid=${uuid}`)
                if (response.data.data) {
                    return response.data.data.code as string;
                }
                return null;
            } catch (error) {
                return null;
            }
        },
        async isTermsCodeValid(phoneNumber: String, code: string): Promise<boolean> {
            try {
                const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/v1/rca-offers/validate-terms-confirm-code`, 
                    {
                        phone_number: phoneNumber,
                        code: code
                    })
                if (response.data.data) {
                    return response.data.data.is_valid as boolean;
                }
                return false;
            } catch (error) {
                return false;
            }
        }
    },
    persist: {
        storage: persistedState.sessionStorage,
        paths: [
            'availabilities',
            'leasingCompanies',
            'allInsuranceCompanies',
        ],
        key: 'rcaStore',
    }
});
